import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { UserContext } from '@providers/userContextProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';

type TransferTypeOption = {
  required?: FieldRequiredType;
  label?: unknown;
};

export type DepositExternalTransferTypeSelectProp = {
  transferType?: string;
  option?: TransferTypeOption;
  focused: string[];
  onFocus: (field: string) => void;
  onChange: (value: Partial<TransferContextData>) => void;
  usTransferFlowEnabled: boolean;
  enableInKind: boolean;
};

export const DepositExternalTransferTypeSelect = ({
  transferType,
  option,
  focused,
  onFocus,
  onChange,
  usTransferFlowEnabled,
  enableInKind,
}: DepositExternalTransferTypeSelectProp) => {
  const { t } = useTranslation('workflowCompletions');
  const { translateBackendString } = useLocalization();
  const { activeOrganization } = useContext(UserContext);
  const hasError =
    !transferType &&
    focused.includes('transferType') &&
    option?.required !== FieldRequiredType.NOT_REQUIRED;

  if (!usTransferFlowEnabled) {
    return (
      <SelectField
        fullWidth
        sx={{ mb: 2, mt: 2 }}
        testId='transfer-type-select'
        label={translateBackendString(option?.label)}
        onChange={(e: any) => onChange({ transferType: e.target.value })}
        value={transferType}
        onBlur={() => onFocus('transferType')}
        error={hasError}
      >
        <MenuItem
          data-testid='transfer-type-select-all-cash'
          value='ALL_IN_CASH'
        >
          {t('depositReview.ALL_IN_CASH')}
        </MenuItem>
        <MenuItem
          data-testid='transfer-type-select-partial-cash'
          value='PARTIAL_CASH'
        >
          {t('depositReview.PARTIAL_CASH')}
        </MenuItem>
        {enableInKind && (
          <MenuItem
            data-testid='transfer-type-select-all-in-kind'
            value='ALL_IN_KIND'
          >
            {t('depositReview.ALL_IN_KIND')}
          </MenuItem>
        )}
      </SelectField>
    );
  }

  return (
    <SelectField
      fullWidth
      sx={{ mb: 2, mt: 2 }}
      testId='transfer-type-select'
      label={translateBackendString(option?.label)}
      onChange={(e: any) => onChange({ transferType: e.target.value })}
      value={transferType}
      onBlur={() => onFocus('transferType')}
      error={hasError}
    >
      {activeOrganization?.enableFullCashTransfers && (
        <MenuItem
          data-testid='transfer-type-select-all-cash'
          value='ALL_IN_CASH'
        >
          {t('depositReview.ALL_IN_CASH')}
        </MenuItem>
      )}
      {activeOrganization?.enablePartialCashTransfers && (
        <MenuItem
          data-testid='transfer-type-select-partial-cash'
          value='PARTIAL_CASH'
        >
          {t('depositReview.PARTIAL_CASH')}
        </MenuItem>
      )}
      {activeOrganization?.enableAllInKindTransfers && enableInKind && (
        <MenuItem
          data-testid='transfer-type-select-all-in-kind'
          value='ALL_IN_KIND'
        >
          {t('depositReview.ALL_IN_KIND')}
        </MenuItem>
      )}
    </SelectField>
  );
};
